import { useLanguage } from '../../../contexts/LanguageContext';
import { translations } from '../../../mocks/translations';

export default function AddressSection() {
  const { language } = useLanguage();
  const t = translations[language].address;

  return (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">{t.title}</h2>
        <p className="text-lg text-gray-600">{t.subtitle}</p>
      </div>

      {/* Location Image Banner */}
      <div className="relative h-64 lg:h-80 rounded-2xl overflow-hidden mb-8">
        <img 
          src="https://public.readdy.ai/ai/img_res/489a32f2e1333e78b10de4a88632a912.jpg"
          alt="Ras Al Khaimah Business District"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/70 to-transparent"></div>
        <div className="absolute bottom-6 left-6 lg:bottom-10 lg:left-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg">
              <i className="ri-map-pin-line text-xl text-white"></i>
            </div>
            <span className="text-white font-bold text-xl lg:text-2xl">Ras Al Khaimah, UAE</span>
          </div>
          <p className="text-white/80 text-sm lg:text-base ml-13">AL Hamra Industrial Zone-FZ</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                <i className="ri-map-pin-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t.registeredAddress}</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Compass Building,<br />
                  Al Shohada Road,<br />
                  AL Hamra Industrial Zone-FZ,<br />
                  Ras Al Khaimah,<br />
                  United Arab Emirates
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 pt-6 border-t border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                <i className="ri-building-2-line text-2xl text-emerald-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t.building}</h3>
                <p className="text-base text-gray-700">Compass Building</p>
              </div>
            </div>

            <div className="flex items-start gap-4 pt-6 border-t border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                <i className="ri-road-map-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t.street}</h3>
                <p className="text-base text-gray-700">Al Shohada Road</p>
              </div>
            </div>

            <div className="flex items-start gap-4 pt-6 border-t border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                <i className="ri-community-line text-2xl text-emerald-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t.zone}</h3>
                <p className="text-base text-gray-700">AL Hamra Industrial Zone-FZ</p>
              </div>
            </div>

            <div className="flex items-start gap-4 pt-6 border-t border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                <i className="ri-map-2-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t.emirate}</h3>
                <p className="text-base text-gray-700">Ras Al Khaimah, United Arab Emirates</p>
              </div>
            </div>

            <div className="flex items-start gap-4 pt-6 border-t border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                <i className="ri-mail-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{t.email}</h3>
                <a href="mailto:info@qiutomsenergy.com" className="text-base text-blue-600 hover:text-blue-700 transition-colors cursor-pointer">
                  info@qiutomsenergy.com
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Building Image */}
          <div className="relative h-64 rounded-xl overflow-hidden shadow-lg">
            <img 
              src="https://readdy.ai/api/search-image?query=Modern%20commercial%20office%20building%20in%20UAE%20free%20zone%2C%20contemporary%20glass%20and%20steel%20architecture%2C%20professional%20business%20center%2C%20clear%20blue%20sky%2C%20palm%20trees%20in%20foreground%2C%20clean%20corporate%20style&width=600&height=300&seq=address2&orientation=landscape"
              alt="Office Building"
              className="w-full h-full object-cover object-top"
            />
          </div>
          
          {/* Map */}
          <div className="bg-gray-100 rounded-xl overflow-hidden h-[300px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14400!2d55.7327!3d25.6867!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ef5f5c1b1b1b1b1%3A0x1234567890abcdef!2sAl%20Hamra%20Industrial%20Zone!5e0!3m2!1sen!2sae!4v1700000000000"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="QIUTOMS ENRGY Location - AL Hamra Industrial Zone, Ras Al Khaimah"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
}
